import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Globe, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";

/**
 * StripeCheckoutCard — global card-payment rail alongside M-Pesa Paybill.
 *
 * Renders the two available Stripe packages (Advanced monthly, Institutional
 * monthly), lets the user click "Subscribe with Card", and redirects to the
 * Stripe Checkout URL. On return, if `?stripe_session_id=…` is in the URL,
 * we poll /api/stripe/status until it flips to paid and refresh the auth
 * context so the tier badge updates.
 */
export default function StripeCheckoutCard() {
    const [packages, setPackages]     = useState([]);
    const [busyId, setBusyId]         = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [pollStatus, setPollStatus] = useState(null); // 'pending' | 'paid' | 'cancelled'
    const [rates, setRates] = useState({ usdkes: null, eurkes: null });

    useEffect(() => {
        Promise.all([
            api.get("/stripe/packages"),
            api.get("/market/ticker/USDKES").catch(() => null),
            api.get("/market/ticker/EURKES").catch(() => null),
        ]).then(([pkgRes, u, e]) => {
            setPackages(pkgRes.data?.packages || []);
            setRates({
                usdkes: u?.data?.price || 129.5,
                eurkes: e?.data?.price || 141.2,
            });
        }).catch(() => {});
    }, []);

    // Post-return handling — if ?stripe_session_id is present, poll status.
    useEffect(() => {
        const sessionId = searchParams.get("stripe_session_id");
        const cancelled = searchParams.get("stripe_cancelled");
        if (cancelled) {
            setPollStatus("cancelled");
            return;
        }
        if (!sessionId) return;

        setPollStatus("pending");
        let stopped = false;
        let attempts = 0;
        async function poll() {
            while (!stopped && attempts < 30) {   // ~60 s max
                attempts++;
                try {
                    const { data } = await api.get(`/stripe/status/${sessionId}`);
                    if (data.payment_status === "paid") {
                        setPollStatus("paid");
                        toast.success("Subscription active — welcome to the tier!");
                        // Clear the query params so back navigation stays clean.
                        setSearchParams({});
                        return;
                    }
                } catch (_e) { /* silent — poll again */ }
                await new Promise((r) => setTimeout(r, 2000));
            }
            if (!stopped && pollStatus === "pending") {
                setPollStatus("timeout");
            }
        }
        poll();
        return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    async function subscribe(pkg) {
        setBusyId(pkg.id);
        try {
            const { data } = await api.post("/stripe/checkout", {
                package_id: pkg.id,
                origin_url: window.location.origin,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Couldn't open Stripe. Try again.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Stripe checkout failed");
        } finally { setBusyId(null); }
    }

    if (!packages.length) return null;

    return (
        <div className="border border-border bg-card rounded-xl p-4" data-testid="stripe-checkout-card">
            <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Globe className="w-4 h-4" />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                        Pay with card · Global
                    </div>
                    <div className="text-sm font-medium">Subscribe from anywhere</div>
                    <div className="text-xs text-muted-foreground">
                        Prefer M-Pesa? Use the Paybill option below — same tier, same benefits.
                    </div>
                </div>
            </div>

            {pollStatus === "paid" && (
                <div className="mb-3 flex items-center gap-2 text-sm text-emerald-500 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4" /> Payment confirmed. Your new tier is active.
                </div>
            )}
            {pollStatus === "cancelled" && (
                <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2">
                    <XCircle className="w-4 h-4" /> Checkout cancelled — no charge made.
                </div>
            )}
            {pollStatus === "pending" && (
                <div className="mb-3 flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    Awaiting Stripe confirmation…
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {packages.map((p) => (
                    <div key={p.id} className="border border-border rounded-lg p-3 flex flex-col"
                         data-testid={`stripe-package-${p.id}`}>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">{p.grants_tier}</div>
                        <div className="text-base font-semibold mt-1">{p.label}</div>
                        <div className="mono text-xl font-bold mt-2">
                            ${(p.amounts?.[p.default_currency] ?? p.amounts?.usd ?? 0).toFixed(2)}<span className="text-xs text-muted-foreground font-normal ml-1">/mo</span>
                        </div>
                        {rates.usdkes && (() => {
                            const usd = p.amounts?.usd;
                            const eur = p.amounts?.eur;
                            const kesFromUsd = usd != null ? Math.round(usd * rates.usdkes) : null;
                            const kesFromEur = eur != null && rates.eurkes ? Math.round(eur * rates.eurkes) : null;
                            return (
                                <div className="text-[11px] text-muted-foreground mono mt-0.5 space-x-2" data-testid={`stripe-package-${p.id}-kes`}>
                                    {kesFromUsd != null && <span>≈ KES {kesFromUsd.toLocaleString()}</span>}
                                    {kesFromEur != null && <span className="opacity-60">· €{eur} ≈ KES {kesFromEur.toLocaleString()}</span>}
                                </div>
                            );
                        })()}
                        <Button
                            className="mt-3"
                            onClick={() => subscribe(p)}
                            disabled={busyId === p.id}
                            data-testid={`stripe-subscribe-${p.id}`}
                        >
                            <CreditCard className="w-4 h-4 mr-1.5" />
                            {busyId === p.id ? "Redirecting…" : "Subscribe with Card"}
                            <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-60" />
                        </Button>
                    </div>
                ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">
                Test card: <span className="mono">4242 4242 4242 4242</span> · any future expiry · any CVC.
            </p>
        </div>
    );
}
